import { useMemo } from 'react';
import { genAttributes } from '@onward-delivery/core';
import { useEditModalTabFields } from '../../../hooks/modal';
import {
    adminErrors,
    allValid,
    errorsByTab,
    fieldErrors,
    itemErrors,
    phoneWarnings,
    referenceErrors,
    requiredFields,
    serviceErrors,
} from '../utils/validation';

/**
 * What blocks Done on the Edit Order modal, and which tabs to mark. The rules are in `validation.js`;
 * this memoizes them against the draft.
 *
 * @param {object} args
 * @param {object} args.draft From `useOrderDraft` (`clone`, `overrides`, `baseOrder`, `isDirty`).
 * @param {string[]} args.tabs The tabs the modal shows.
 * @param {string[]|((order: object) => string[])} [args.requiredAttributes] The caller's required fields.
 * @param {{ disableGeocoding?: boolean, startDirty?: boolean }} args.options Merged display options.
 * @param {object} args.serviceCatalog The loaded service catalog.
 * @returns {{
 *   required: string[],
 *   hasError: Object<string, true>,
 *   hasWarning: Object<string, true>,
 *   itemHasError: object[],
 *   adminHasError: Object<string, boolean>,
 *   hasErrorTab: Object<string, boolean>,
 *   isValid: boolean,
 * }}
 */
export const useOrderValidation = ({ draft, tabs, requiredAttributes, options, serviceCatalog }) => {
    const { clone, overrides, baseOrder, isDirty } = draft;
    const { disableGeocoding, startDirty } = options;

    const fieldsByTab = useEditModalTabFields(clone);
    const originAttrs = useMemo(() => genAttributes(clone || {}, 'ORIGIN'), [clone]);

    const required = useMemo(
        () => requiredFields(clone, { requiredAttributes, disableGeocoding, tabs, originAttrs }),
        [clone, requiredAttributes, disableGeocoding, tabs, originAttrs]
    );
    const hasError = useMemo(
        () => fieldErrors(clone, required, { tabs, isDirty, originAttrs, disableGeocoding }),
        [clone, required, tabs, isDirty, originAttrs, disableGeocoding]
    );
    const hasWarning = useMemo(() => phoneWarnings(clone), [clone]);
    const itemHasError = useMemo(() => itemErrors(clone), [clone]);
    const adminHasError = useMemo(
        () => adminErrors({ clone, overrides, base: baseOrder, tabs }),
        [clone, overrides, baseOrder, tabs]
    );
    const serviceHasError = useMemo(() => serviceErrors(clone, serviceCatalog), [clone, serviceCatalog]);
    const referenceHasError = useMemo(() => referenceErrors(clone), [clone]);

    const hasErrorTab = useMemo(
        () =>
            errorsByTab({
                fieldsByTab,
                hasError,
                itemHasError,
                isDirty,
                adminHasError,
                serviceHasError,
                referenceHasError,
                startDirty,
            }),
        [fieldsByTab, hasError, itemHasError, isDirty, adminHasError, serviceHasError, referenceHasError, startDirty]
    );

    const isValid = useMemo(
        () => allValid({ hasError, itemHasError, adminHasError, serviceHasError, referenceHasError }),
        [hasError, itemHasError, adminHasError, serviceHasError, referenceHasError]
    );

    return { required, hasError, hasWarning, itemHasError, adminHasError, hasErrorTab, isValid };
};
